/**
 * ProductSuggestPicker — single-select catalog product picker with context suggestions.
 *
 * @description
 *   Single-select sibling of ParentProductPicker. Renders ranked suggestion
 *   chips (context-derived, top highlighted) plus a manual Autocomplete search.
 *   Caller supplies suggestions + a search function; the component owns only
 *   selection + search-input UI. Built for the discovery inbox "assign product"
 *   step but reusable anywhere a single product must be chosen from context.
 *
 * @useWhen
 *   - A reviewer must pick exactly ONE catalog product, aided by ranked suggestions
 *   - Discovery candidate SKU assignment; RMA replacement pick; PO line pick
 * @dontUseFor
 *   - Multi-select parent linking — use ParentProductPicker
 *   - Pure free-text with no catalog validation
 * @related
 *   - ParentProductPicker — multi-select spare-part sibling
 * @reuseHints
 *   - RMA "assign replacement product"; PO "add line item by product"
 * @api value, onChange, suggestions, suggestLoading, onSearch
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Stack, Typography, Chip, Autocomplete, TextField, Skeleton, Alert,
} from '@mui/material';
import { FS } from '../../theme/tokens';

export default function ProductSuggestPicker({
  value,
  onChange,
  suggestions = [],
  suggestLoading = false,
  onSearch,
}) {
  const [input, setInput] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!input || input.length < 2) { setResults([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await onSearch(input);
        if (active) setResults(Array.isArray(r) ? r : []);
      } finally {
        if (active) setSearching(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [input, onSearch]);

  const selectedSku = value?.sku || null;

  return (
    <Box>
      <Autocomplete
        options={results}
        value={value}
        onChange={(_, v) => onChange(v || null)}
        onInputChange={(_, v) => setInput(v)}
        getOptionLabel={(o) => (o ? `${o.product_name || ''} (${o.sku})` : '')}
        isOptionEqualToValue={(o, v) => o?.sku === v?.sku}
        loading={searching}
        renderInput={(params) => (
          <TextField {...params} size="small" label="Assign product"
                     placeholder="Search catalog by name or SKU…" />
        )}
      />

      {suggestLoading && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          {[0, 1, 2].map(i => <Skeleton key={i} variant="rounded" width={150} height={28} />)}
        </Stack>
      )}

      {!suggestLoading && suggestions.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Suggested from the listing:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {suggestions.map((s, idx) => {
              const pct = typeof s.confidence === 'number'
                ? ` (${Math.round(s.confidence * 100)}%)` : '';
              const selected = selectedSku === s.sku;
              return (
                <Chip
                  key={s.sku}
                  label={`${s.product_name}${pct}`}
                  title={s.reason || ''}
                  size="small"
                  clickable
                  onClick={() => onChange(selected ? null : { sku: s.sku, product_name: s.product_name })}
                  variant={selected ? 'filled' : 'outlined'}
                  // idx 0 = top suggestion → primary color
                  color={selected ? 'primary' : (idx === 0 ? 'primary' : 'default')}
                  sx={{ cursor: 'pointer' }}
                />
              );
            })}
          </Stack>
        </Box>
      )}

      {!suggestLoading && suggestions.length === 0 && (
        <Alert severity="info" variant="outlined" icon={false} sx={{ mt: 1, fontSize: FS.md }}>
          No suggestions — search manually to assign a product.
        </Alert>
      )}
    </Box>
  );
}
